import { format, isSameDay, isSameYear, parseISO } from 'date-fns';

import type { NoteItem, SessionRef } from '@/store/api/mcpMemberApi';

import { formatDateLabel, toDateSlug } from '@/utils/dateSlug';

const parse = (iso: string): Date | null => {
  const date = parseISO(iso);
  return Number.isNaN(date.getTime()) ? null : date;
};

// "Today", "Mar 5" (this year) or "Nov 26, 2025".
export const dayLabel = (iso: string, now: Date = new Date()): string => {
  const date = parse(iso);
  if (!date) {
    return '';
  }
  if (isSameDay(date, now)) {
    return 'Today';
  }
  return format(date, isSameYear(date, now) ? 'MMM d' : 'MMM d, yyyy');
};

export const updatedLabel = (
  iso: string | undefined,
  now: Date = new Date(),
): string => {
  const label = iso ? dayLabel(iso, now) : '';
  if (!label) {
    return '';
  }
  return label === 'Today' ? 'Updated today' : `Updated ${label}`;
};

export const latestUpdatedAt = (items: NoteItem[]): string | undefined =>
  items.reduce<string | undefined>(
    (latest, item) =>
      !latest || Date.parse(item.updated_at) > Date.parse(latest)
        ? item.updated_at
        : latest,
    undefined,
  );

// Session days are calendar days: read the ISO date prefix so a midnight-UTC
// date does not shift to the previous day in US time zones.
export const longDate = (ref: SessionRef): string =>
  formatDateLabel(toDateSlug(ref.date, '')) ?? ref.label;

export const reflectionPath = (ref: SessionRef): string =>
  `/m/artifacts/session-reflection/${encodeURIComponent(ref.artifact_id)}/${toDateSlug(ref.date, 'reflection')}`;

// Prototype preview(): drop the generated lead-in, capitalize the rest.
const KEY_INSIGHT_LEAD_IN =
  /^(A thread running through (the|your) (conversation|sessions) (was|is) |What started to come into focus was |Across the conversation, you began to see )/u;

export const keyInsightPreview = (keyInsight: string): string => {
  const text = keyInsight.replace(KEY_INSIGHT_LEAD_IN, '');
  return text.charAt(0).toUpperCase() + text.slice(1);
};
